// ---------------------------------------------------------------------------
// Plan-tier feature gating
// ---------------------------------------------------------------------------
//
// A company's package lives on `Company.planName`. AccountService stamps it
// onto every /account/getUser response for the EFFECTIVE company, so a
// multi-location owner who switches locations gets the plan of the location
// they are currently standing in — read it off `userInfo`, never off a
// remembered value.
//
// Which of these trims apply is no longer "is this Basic?" — it is a set of
// per-plan checkboxes in Manage Company > Plans & Pricing (Advanced Weekly
// Report, Totals by Machine Type, Black List, Match Privilege, Change
// History). Ask planHasCapability, not the plan name.
//
// Out of the box the answer is the same one this module always gave: Basic
// is the entry package and buys a BASIC Weekly Report — the day-to-day money
// tabs only, no analysis, no manager loan board, no reading roll-up, two
// headline percentages, and no change history anywhere in the app — while
// Regular, Premium and Alpha get the full report. isBasicPlan stays as the
// fallback for a plan the catalog cannot resolve.
//
// Role-based gating stays where it is — this module answers "what did the
// company buy", not "what is this employee allowed to touch". Both apply:
// a Basic manager needs ROLE_REPORT to reach the screen AND passes
// through these filters once they are on it.
//
// Server-side mirror: wizardConfig.PLAN_ROLES decides which authorities a
// Basic company may hold at all, which is what actually keeps the data
// out of reach. These helpers shape the UI on top of that.

import {
  normalizePlanName,
  planByName,
  PLAN_CAPABILITIES,
} from '../components/settings/ManageCompany/wizardConfig';

export const BASIC_PLAN = 'Basic';

// Capability keys, matching PlanCatalogDefaults.FEATURES on the server.
// 'fullWeeklyReport' is the one asked about from three places, so it gets a
// name rather than three string literals that could drift apart.
export const FULL_WEEKLY_REPORT = 'fullWeeklyReport';

/**
 * Does the effective company's plan carry this capability?
 *
 * These trims used to be a single question — "is this Basic?" — with the
 * answer wired into six helpers. They are now per-capability rows an
 * operator ticks in Manage Company > Plans & Pricing, so ask the plan.
 *
 * Falls back to the Basic rule while the live rate card is still loading
 * (PLAN_CAPABILITIES is empty until then) and for a company whose plan name
 * matches nothing in the catalog. Both fall back to what the app did before
 * the catalog existed, so an unresolvable plan never silently upgrades or
 * downgrades what a user can see.
 */
export function planHasCapability(userInfo, capability) {
  const plan = planByName(userInfo?.planName);
  const caps = plan ? PLAN_CAPABILITIES[plan.id] : null;
  if (!caps) return !isBasicPlan(userInfo);
  return caps.includes(capability);
}

/**
 * True when the effective company is on the Basic package.
 *
 * Goes through normalizePlanName because the tier NAMES shifted down a
 * rung in the plan rename (old "Standard" is today's "Basic", and old
 * "Premium" is today's "Regular"). A company that has not been migrated
 * yet still carries the old string, so comparing raw would let a legacy
 * Standard company keep the full report.
 */
export function isBasicPlan(userInfo) {
  return normalizePlanName(userInfo?.planName) === BASIC_PLAN;
}

// Weekly Report tabs included in the Basic package, keyed by the `key`
// field of WEEKLY_TABS in accounting/weeklyReport/ReportView.jsx. Manager
// Loan and Daily Analysis are the two paid-tier tabs.
export const BASIC_WEEKLY_TABS = new Set([
  'profit', // Cash Flow
  'readings', // minus the machine-type roll-up, see below
  'expenses',
  'closingBalance',
  'percentages', // trimmed to the two fields below
]);

/** Weekly Report tabs this user's plan may open. */
export function allowedWeeklyTabs(userInfo, tabs) {
  if (planHasCapability(userInfo, FULL_WEEKLY_REPORT)) return tabs;
  return tabs.filter((t) => BASIC_WEEKLY_TABS.has(t.key));
}

// The Percentages tab collapses to the two numbers an operator actually
// runs the floor on: how much of the handle went back out as matches, and
// how much of it the machines held.
export const BASIC_EXPENSE_PCT_FIELDS = new Set(['matchPct']);
export const BASIC_MACHINE_PCT_FIELDS = new Set(['machineHoldPct']);

/** Percentage rows (Expense % / Machine %) this user's plan may see. */
export function allowedPercentageRows(userInfo, rows, allowedFields) {
  if (planHasCapability(userInfo, FULL_WEEKLY_REPORT)) return rows;
  return rows.filter((r) => allowedFields.has(r.field));
}

// "Totals by Machine Type" — the roll-up card pinned under the Readings
// tab. Paid tiers only; the per-day reading numbers above it stay.
export function canViewReadingSummary(userInfo) {
  return planHasCapability(userInfo, 'readingSummary');
}

// The paid-tier Weekly Report sections, on screen AND in the downloaded
// PDF: Manager Loan to Bank, Daily Analysis (Equation X, pulls comparison,
// match tickets, per-employee shorts) and the Meter Pulls breakdown that
// feeds it. The PDF has to ask the same question the tabs do -- a hidden
// tab that a Basic user can still download is not hidden.
export function hasFullWeeklyReport(userInfo) {
  return planHasCapability(userInfo, FULL_WEEKLY_REPORT);
}

/**
 * The Black List. A paid-tier feature: it is listed under Regular's
 * Accounting section, not Basic's, so a Basic company does not get it.
 *
 * Every blacklist surface must ask this, not just the obvious one. They
 * are easy to miss because they do not look like a list: the Banned option
 * in the customer status menu WRITES the flag, and the duplicate-check
 * panel on the Add Customer form READS it. Hiding only the tab left a
 * Basic company able to ban customers and see blacklist warnings for a
 * feature it never bought.
 *
 * The exception is a user carrying ROLE_SECURITY — the security-dashboard
 * capability — who keeps blacklist access whatever the plan says.
 */
export function canUseBlacklist(userInfo) {
  if (planHasCapability(userInfo, 'blacklist')) return true;
  return !!userInfo?.authority?.includes('ROLE_SECURITY');
}

/**
 * Match Privilege — the per-customer match rules (time limit, matches per
 * day, cap, match values). Listed under Regular's Accounting section, so
 * Basic does not get it.
 */
export function canUseMatchRules(userInfo) {
  return planHasCapability(userInfo, 'matchRules');
}

// Change history — the Weekly Report audit log and the Master Reading
// audit log. Both are paid-tier; Basic sees the current state only.
export function canViewAuditHistory(userInfo) {
  return planHasCapability(userInfo, 'auditHistory');
}
